'use client';
import React, { Suspense } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import type { BaseQuotePageProps, BaseQuoteProfile } from '../../../../../src/utils/quote/types';
import type { DividendSummary } from '../../../../../src/utils/quote/summaries';
import type { QuoteSessionType } from '@benzinga/quotes-manager';

import { SectionTitle } from '@benzinga/core-ui';
import { formatPercentage, numberShorthand } from '@benzinga/utils/numberUtils';
import type { Financials } from '@benzinga/securities-manager';
import { formatProfileDataToStockFundamentalsData } from '../../../../../src/components/Quote/utils';
import { KPIBox } from '../../../../../src/components/Quote/KPIBox';
import DrilldownFooter from '../../../../../src/components/Quote/DrilldownFooter';
import DividendsFAQ from '../../../../../src/components/Quote/FAQ/DividendsFAQ';
import { DividendsHistoryTable } from '../../../../../src/components/Quote/DividendsHistoryTable';
import { QuoteFooter } from '../../../../../src/components/Quote/QuoteFooter';
import SeeMoreLink from '../_components/SeeMoreLink';
import { seeMoreDestinations } from '../_utils/seeMoreDestinations';

interface DividendsPageProps extends BaseQuotePageProps {
  dividendSummary: DividendSummary | null;
  fundamentals: Financials | null;
  fundamentalsAnnual?: Financials | null;
}

const FreestarAd = React.lazy(() =>
  import('@benzinga/ads').then(module => ({ default: module.FreestarAd })),
);

const sanitizeHTML: any = (str: string) => str;

const formatDate = (date: string, language: string) => {
  const d = DateTime.fromISO(date);
  let format = 'MMM d';
  if (d < DateTime.now().minus({ year: 1 })) {
    format = 'MMM d, y';
  }
  return date ? DateTime.fromISO(date).setLocale(language).toFormat(format) : 'N/A';
};

const DividendSummaryBox: React.FC<{
  dividendSummary: DividendSummary;
  profile: BaseQuoteProfile;
  fundamentals?: Financials | null;
  fundamentalsAnnual?: Financials | null;
}> = ({ dividendSummary, profile, fundamentals, fundamentalsAnnual }) => {
  const { t, i18n } = useTranslation(['quote', 'common']);
  const stock = formatProfileDataToStockFundamentalsData({ fundamentals, fundamentalsAnnual, profile });

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {stock?.dividendYield && (
        <KPIBox
          label={`${t('Quote.Dividend.dividend-yield')} (${t('Quote.Dividend.forward')})`}
          value={`${formatPercentage(Number(stock.dividendYield))}%`}
        />
      )}
      {!!stock?.dividend && (
        <KPIBox
          label={t('Quote.Dividend.annual-dividend')}
          value={`$${numberShorthand(stock.forwardDividend, 2)}`}
        />
      )}
      {dividendSummary?.last?.record_date && (
        <KPIBox
          label={t('Quote.Dividend.last-dividend')}
          value={formatDate(dividendSummary.last?.record_date, i18n.language)}
        />
      )}
      {dividendSummary?.next?.ex_dividend_date && (
        <KPIBox
          label={t('Quote.Dividend.next-dividend')}
          value={formatDate(dividendSummary.next?.ex_dividend_date, i18n.language)}
        />
      )}
    </div>
  );
};

const DividendsContent: React.FC<DividendsPageProps> = ({
  dividendSummary,
  metaProps,
  profile,
  symbol,
  fundamentals,
  fundamentalsAnnual,
}) => {
  const { t, i18n } = useTranslation('quote');

  const stock = formatProfileDataToStockFundamentalsData({ fundamentals, fundamentalsAnnual, profile });

  const dividendFrequencyNumber = dividendSummary?.dividends.find(dividend => dividend.frequency !== 0)?.frequency;
  const dividendFrequency: string =
    dividendFrequencyNumber === 12
      ? t('Date.Periods.monthly')
      : dividendFrequencyNumber === 4
        ? t('Date.Periods.quarterly')
        : dividendFrequencyNumber === 2
          ? t('Date.Periods.semi-annually')
          : dividendFrequencyNumber === 1
            ? t('Date.Periods.annual')
            : dividendFrequencyNumber?.toString() || '-';

  const lastExDividendDate = dividendSummary?.last?.ex_dividend_date;
  const nextExDividendDate = dividendSummary?.next?.ex_dividend_date;

  const generateOverviewDescription = (): string => {
    if (!stock.forwardDividend) {
      return t('Quote.Dividend.dividend-overview-description-no-dividend', {
        companyName: profile?.richQuoteData?.companyStandardName,
      });
    }
    return t('Quote.Dividend.dividend-overview-description', {
      annualDividend: stock.dividend ? `$${numberShorthand(stock.forwardDividend, 2)}` : '-',
      companyName: profile?.richQuoteData?.companyStandardName,
      dividendFrequency: dividendFrequency?.toLocaleLowerCase() || '-',
      dividendYield: stock.dividendYield ? formatPercentage(Number(stock.dividendYield)) : '-',
    });
  };

  const generateOverviewDescriptionSecond = (): string => {
    if (!lastExDividendDate && !nextExDividendDate) {
      return t('Quote.Dividend.dividend-overview-description-2-both-dates-unknown', {
        companyName: profile?.richQuoteData?.companyStandardName,
      });
    }
    if (!nextExDividendDate) {
      return t('Quote.Dividend.dividend-overview-description-2-next-date-unknown', {
        companyName: profile?.richQuoteData?.companyStandardName,
        lastDividendDate: lastExDividendDate ? formatDate(lastExDividendDate, i18n.language) : '-',
        nextDividendDate: nextExDividendDate ? formatDate(nextExDividendDate, i18n.language) : '-',
      });
    } else if (!lastExDividendDate) {
      return t('Quote.Dividend.dividend-overview-description-2-last-date-unknown', {
        companyName: profile?.richQuoteData?.companyStandardName,
        lastDividendDate: lastExDividendDate ? formatDate(lastExDividendDate, i18n.language) : '-',
        nextDividendDate: nextExDividendDate ? formatDate(nextExDividendDate, i18n.language) : '-',
      });
    }
    return t('Quote.Dividend.dividend-overview-description-2', {
      companyName: profile?.richQuoteData?.companyStandardName,
      lastDividendDate: lastExDividendDate ? formatDate(lastExDividendDate, i18n.language) : '-',
      nextDividendDate: nextExDividendDate ? formatDate(nextExDividendDate, i18n.language) : '-',
    });
  };

  const generateKeyHighlights = () => {
    return (
      <ul className="md:ml-8 list-disc">
        <li>
          <strong>{t('Quote.Dividend.KeyHighlights.annual-payout')}:</strong>{' '}
          {stock.dividend ? `$${numberShorthand(stock.forwardDividend, 2)}` : '-'}
        </li>
        <li>
          <strong>{t('Quote.Dividend.KeyHighlights.current-yield')}:</strong>{' '}
          {stock.dividendYield ? `${formatPercentage(Number(stock.dividendYield))}%` : '-'}
        </li>
        <li>
          <strong>{t('Quote.Dividend.KeyHighlights.next-ex-dividend-date')}:</strong>{' '}
          {nextExDividendDate ? formatDate(nextExDividendDate, i18n.language) : 'Unknown'}
        </li>
        <li>
          <strong>{t('Quote.Dividend.KeyHighlights.last-ex-dividend-date')}:</strong>{' '}
          {lastExDividendDate ? formatDate(lastExDividendDate, i18n.language) : 'Unknown'}
        </li>
        <li>
          <strong>{t('Quote.Dividend.KeyHighlights.payments-per-year')}:</strong> {dividendFrequencyNumber || '-'}
        </li>
      </ul>
    );
  };

  const overviewDescription = generateOverviewDescription();
  const overviewDescriptionSecond = generateOverviewDescriptionSecond();
  const keyHighlights = generateKeyHighlights();

  return (
    <>
      <div className="main-div px-4">
        <div className="flex flex-col gap-2 mb-8">
          <SectionTitle level={1} uppercase={false}>
            {metaProps.title}
          </SectionTitle>
          <p className="section-description">
            {t('Quote.Paragraphs.dividend', { stockName: profile?.richQuoteData?.name })}
          </p>
          <SectionTitle level={2} uppercase={false}>
            {t('Quote.Dividend.dividend-overview-title', {
              companyName: profile?.richQuoteData?.companyStandardName,
            })}
          </SectionTitle>
          <p className="section-description" dangerouslySetInnerHTML={{ __html: sanitizeHTML(overviewDescription) }} />
          <p
            className="section-description"
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(overviewDescriptionSecond) }}
          />
          <SectionTitle level={2} uppercase={false}>
            {t('Quote.Dividend.key-highlights-title')}:
          </SectionTitle>
          {keyHighlights}
        </div>
        {dividendSummary && (
          <DividendSummaryBox
            dividendSummary={dividendSummary}
            fundamentals={fundamentals}
            fundamentalsAnnual={fundamentalsAnnual}
            profile={profile}
          />
        )}
      </div>
      <div className="main-div p-4">
        <div className="flex items-baseline justify-between gap-2">
          <SectionTitle level={2}>
            {t('Quote.Dividend.dividends-for', { companyName: profile?.richQuoteData?.name })}
          </SectionTitle>
          {(dividendSummary?.dividends?.length ?? 0) > 0 && (
            <SeeMoreLink
              action="see_more_dividends"
              href={seeMoreDestinations.dividends(symbol)}
              label={t('Quote.SeeMore.dividends')}
            />
          )}
        </div>
      </div>
      <div className="main-div p-4 flex flex-col gap-y-8">
        <DividendsHistoryTable dividends={dividendSummary?.dividends ?? []} />
        <Suspense fallback={<div />}>
          <FreestarAd className="w-[300px] min-w-[300px]" onlyMobile={true} placement="incontent" />
        </Suspense>
        <DrilldownFooter
          isETF={profile.isETF}
          leftSection={
            <>
              <SectionTitle level={3} size="2xl">
                {t('Quote.FAQ.faq')}
              </SectionTitle>
              {dividendSummary && profile?.richQuoteData && (
                <DividendsFAQ dividendSummary={dividendSummary} quote={profile?.richQuoteData} />
              )}
            </>
          }
          peers={profile.peers}
          peerSymbols={profile.peerSymbols}
          symbol={symbol}
          type={profile.schedule?.type ?? ('REGULAR' as QuoteSessionType)}
        />
        <QuoteFooter activeTab="dividends" />
      </div>
    </>
  );
};

export default DividendsContent;
